import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  input,
  linkedSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { IconComponent } from '@app/components/ui-icon/icon.component';

interface UserInput {
  firstName?: string | null;
  lastName?: string | null;
  fullName?: string | null;
  profileImg?: string | null;
}

/**
 * Port of handyhand-web's `ui-avatar`, minus the click-to-expand image modal
 * (this project has no image modal, and the review slider disables it anyway).
 */
@Component({
  selector: 'ui-avatar',
  templateUrl: './avatar.component.html',
  styleUrls: ['./avatar.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [CommonModule, IconComponent],
})
export class AvatarComponent {
  public readonly user = input<UserInput | null>(null);
  public readonly type = input<'circle' | 'square'>('circle');
  public readonly size = input<string>('36px');
  /** Set when an adjacent name already identifies the person, so the image needs no alt text. */
  public readonly decorative = input<boolean>(false);

  // Resets when the image changes, so a reused view retries the new one.
  public readonly imageFailed = linkedSignal<
    string | null | undefined,
    boolean
  >({
    source: () => this.user()?.profileImg,
    computation: () => false,
  });

  public readonly altText = computed(() => {
    if (this.decorative()) {
      return '';
    }
    const user = this.user();
    return (
      user?.fullName ||
      [user?.firstName, user?.lastName].filter((item) => !!item).join(' ')
    );
  });

  public readonly hasImage = computed(
    () => !!this.profileImage() && !this.imageFailed(),
  );

  public readonly profileImage = computed(() =>
    !this.imageFailed() ? this.user()?.profileImg : null,
  );

  public readonly initials = computed(() => {
    const user = this.user();

    if (!user) {
      return null;
    }

    if (user.fullName) {
      const initials = user.fullName
        .split(' ')
        .map((item) => item?.substring(0, 1))
        .slice(0, 2)
        .join('')
        .toUpperCase();

      return initials || null;
    }

    if (user.firstName || user.lastName) {
      const initials = [user.firstName, user.lastName]
        .filter((item) => !!item)
        .map((item) => item?.substring(0, 1))
        .slice(0, 2)
        .join('')
        .toUpperCase();

      return initials || null;
    }

    return null;
  });

  public readonly letterGroup = computed(() => {
    const initials = this.initials();
    const firstLetter: string | null =
      initials && initials.length > 0 ? initials.substring(0, 1) : null;

    const isBetween = (letter: string | null, start: string, end: string) => {
      if (letter === null) {
        return null;
      }

      return (
        letter.localeCompare(start, 'da') >= 0 &&
        letter.localeCompare(end, 'da') <= 0
      );
    };

    if (isBetween(firstLetter, 'A', 'F')) {
      return 'a-f';
    }

    if (isBetween(firstLetter, 'G', 'L')) {
      return 'g-l';
    }

    if (isBetween(firstLetter, 'M', 'R')) {
      return 'm-r';
    }

    if (isBetween(firstLetter, 'S', 'X')) {
      return 's-x';
    }

    if (isBetween(firstLetter, 'Y', 'Å')) {
      return 'y-aa';
    }

    return 'a-f';
  });

  protected onImageFailed(): void {
    this.imageFailed.set(true);
  }
}
